import { createContext } from 'react';
import { AuthState, LoginCredentials, LoginResponse, UserInfo } from '../types';

export interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ requires2FA?: boolean } | void>;
  verify2FA: (code: string) => Promise<void>;
  /**
   * Enter the signed-in state from a LoginResponse obtained OUTSIDE the password flow — the
   * email-verification code/link endpoints sign the user in when they activate the account. Stores the
   * session (AuthService.adoptSession) and dispatches the same LOGIN_SUCCESS as `login`.
   */
  signInWithSession: (login: LoginResponse) => void;
  logout: () => void;
  clearError: () => void;
  /** Optimistically mark first-run onboarding dismissed in-memory after the dismiss API succeeds. */
  markOnboardingDismissed: () => void;
  /**
   * Re-read `/auth/me` (or adopt an already-fetched `UserInfo`) and replace the in-memory user. Used
   * after a self-service profile save so the AppBar/greeting reflect the new name without a reload.
   */
  refreshUser: (fresh?: UserInfo) => Promise<void>;
  pendingCredentials: LoginCredentials | null;
  /**
   * True while a SuperAdmin is impersonating another user. `state.user` is then the TARGET, so all role
   * gating reflects the target; the persistent banner is driven by this flag.
   */
  impersonating: boolean;
  /** The acting SuperAdmin (email) while {@link impersonating}; null otherwise. */
  impersonator: { email: string } | null;
  /** Start impersonating a user (SuperAdmin only): swaps the session token, re-fetches /me as the target. */
  startImpersonation: (userId: number, reason: string) => Promise<void>;
  /** Stop impersonating and restore the admin session (re-fetches /me as the admin). */
  stopImpersonation: () => Promise<void>;
  // Role helper functions
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  // Convenience properties
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** External (Agent Portal) user — row-level access only, never sees DRIS indices/norms. */
  isExternal: boolean;
  /** Partner (Partner Portal) user — submits samples, reads own/granted results. Partner role only. */
  isPartner: boolean;
  /**
   * Partner product manager — holds the PartnerProductManager add-on role, unlocking the
   * per-partner Products surface. A plain Partner is false here.
   */
  isPartnerProductManager: boolean;
  /**
   * Partner API-key manager — holds the PartnerApiKeyManager add-on role, unlocking PAT minting
   * (the API Keys page). A plain Partner is false here.
   */
  isPartnerApiKeyManager: boolean;
  /**
   * Partner admin — holds the PartnerAdmin add-on role, unlocking team management (the Team page).
   * A plain Partner is false here.
   */
  isPartnerAdmin: boolean;
  // Menu access control
  canAccessMenu: (menuId: string) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);